import React, { useEffect } from 'react';
import { X, ExternalLink, Loader2 } from 'lucide-react';
import { useHelpDeskStore, HelpDeskTab } from '../../../stores/helpdesk-store';

interface HelpDeskDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpDeskDrawer: React.FC<HelpDeskDrawerProps> = ({ isOpen, onClose }) => {
  const {
    activeTab,
    items,
    isLoading,
    error,
    hasMore,
    setActiveTab,
    fetchTickets,
    loadMore,
    // refresh,
    clearError,
  } = useHelpDeskStore();

  useEffect(() => {
    if (isOpen) {
      fetchTickets();
    }
  }, [isOpen, activeTab]);

  const tabs: { id: HelpDeskTab; label: string }[] = [
    { id: 'all-tickets', label: 'All' },
    { id: 'open', label: 'Open' },
    { id: 'closed', label: 'Closed' },
    { id: 'solved', label: 'Solved' },
    { id: 'inprogress', label: 'In Progress' },
  ];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'solved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTicketClick = (ticket: any) => {
    if (ticket.help_desk_ticket_url) {
      window.open(ticket.help_desk_ticket_url, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer - Matching Figma design */}
      <div className="absolute right-0 top-0 h-full w-[656px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out">
        {/* Header - Dark blue background */}
        <div className="flex items-center justify-between p-4 bg-secondary-500 text-white">
          <h2 className="text-lg font-semibold">Help Desk</h2>
          <button
            onClick={onClose}
            className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 text-base font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-black border-b-2 border-black'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(960px - 120px)' }}>
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={clearError}
                className="mt-2 text-xs text-red-500 hover:text-red-700"
              >
                Dismiss
              </button>
            </div>
          )}

          {isLoading && items.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading tickets...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No tickets found</p>
            </div>
          ) : (
            <div className="p-4">
              {/* Tickets Table - Matching Figma design */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Ticket Number</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Assigned To</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((ticket) => (
                      <tr 
                        key={ticket.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleTicketClick(ticket)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {ticket.ticket_id}
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{ticket.assign_to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {hasMore && (
                <div className="p-4 text-center">
                  <button
                    onClick={loadMore}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Loading...
                      </div>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpDeskDrawer;
